import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect, useContext, useRef } from 'react'
import { scrollToTop, formatCurrency, redirectAfterTimeout } from '../utilities/general-utils'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {getOBCustomerAccountClientModel} from '../models/OpenBankingCustomerAccountModel'

import { OBCustomerContext } from "../contexts/ob-customer-context";
import OBAccountPreview from "../components/Account/OBAccountPreview";
import {
    svc_getOBCustomerAccount,
    svc_updateOBCustomerAccount,
} from "../services/internal-openbanking.service";
import { IconAngleLeftBold } from "../assets/icons";
import AccountIncludedTooltip from "../components/Account/AccountIncludedTooltip";
import { useResponseMessages } from "../hooks/useResponseMessage";

const AccountOB = () => {
    const { getAccessTokenSilently, user } = useAuth0()
    const { obCustomer } = useContext(OBCustomerContext)
    const navigate = useNavigate()
    const el = useRef(null)
    const [account, setAccount] = useState(null)
    const [originalAccountData, setOriginalAccountData] = useState(null)
    const [hasChanged, setHasChanged] = useState(false)
    const { addMessage } = useResponseMessages();

    let { id: idParam } = useParams()
    const accountId = parseInt(idParam)

    const handleIncludeBalanceChange = (e) => {
        if (account) {
            let changed = false
            let updatedValue = e.target.checked ? 1 : 0

            if (updatedValue !== originalAccountData.includeBalance) {
                changed = true
            }

            setHasChanged(changed)
        }

        setAccount({
            ...account,
            includeBalance: e.target.checked ? 1 : 0
        })
    }

    const getAccount = async () => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await svc_getOBCustomerAccount(accessToken, user.sub, obCustomer.id, accountId);

        if (error) {
            addMessage({
                error: `No account found matching ID ${accountId}`
            })
            return
        }

        let accountData = await data[0]

        accountData = typeof accountData === 'string'
            ? getOBCustomerAccountClientModel(JSON.parse(accountData))
            : getOBCustomerAccountClientModel(accountData)

        setAccount(accountData)
        setOriginalAccountData(accountData)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        const accessToken = await getAccessTokenSilently();
        let errors = []

        const accountData = {
            ...account,
            includeBalance: account.includeBalance ? 1 : 0,
        }

        const { error } = await svc_updateOBCustomerAccount(accessToken, user.sub, accountData);

        if (error) {
            errors.push(error.message)
        }

        if (errors.length > 0) {
            addMessage({
                error: errors.join(', ')
            })
        } else {
            addMessage({
                data: 'Account updated successfully'
            })
            redirectAfterTimeout(window.location.pathname, 2_000, navigate)
        }
        scrollToTop(el.current, -70)
    }

    useEffect(() => {
        if (obCustomer && accountId && !account) {
            getAccount()
        }
    }, [obCustomer, accountId, getAccount])

    return (
        <div className="content-width-container">
            <Link to="/accounts" className="button button-slim button-blue spc-m-b-1 spc-m-t-1">
                <IconAngleLeftBold fill="#fff" /> Back to My Accounts
            </Link>

            <div ref={el} className={`standardFormWrap`}>
                {account && (
                    <>
                        <h1 className="text-center spc-m-t-0 spc-m-b-1">Account Details</h1>

                        <OBAccountPreview account={originalAccountData} disableLink={true} addBorder={true} />
                        
                        {originalAccountData.includeBalance === 1 && (
                            <div className="spc-m-t-1">
                                <p>
                                    The available balance for this account is included in your budget funds.
                                    <AccountIncludedTooltip />
                                </p>
                            </div>
                        )}

                        <form
                            className="formStandard spc-m-t-1"
                            style={{ padding: '0 1rem' }}
                            onSubmit={handleSubmit}
                        >
                            <div className="formField">
                                <label>
                                    <input
                                        defaultChecked={account.includeBalance === 1}
                                        type="checkbox"
                                        onChange={handleIncludeBalanceChange}
                                    />
                                    {' '}Include Available Balance In Budget Funds
                                </label>
                            </div>

                            {hasChanged && (
                                <div className="formField button-row">
                                    <button type="submit" className="button button-green button-slim">
                                        Update
                                    </button>
                                </div>
                            )}

                            <div className="formField">
                                <label>Name*: </label>
                                <div className="mock-disabled-input">
                                    {account.data.name}
                                </div>
                            </div>
            
                            <div className="formField">
                                <label>Institution: </label>
                                <div className="mock-disabled-input">
                                    {account.data.institutionId}
                                </div>
                            </div>

                            <div className="formField">
                                <label>Account Number: </label>
                                <div className="mock-disabled-input">
                                    {account.data.number}
                                </div>
                            </div>
            
                            <div className="formField">
                                <label>Status: </label>
                                <div className="mock-disabled-input">
                                    {account.data.status}
                                </div>
                            </div>
            
                            <div className="formField">
                                <label>Type: </label>
                                <div className="mock-disabled-input">
                                    {account.data.type}
                                </div>
                            </div>

                            <div className="formField">
                                <label>Available Balance: </label>
                                <div className="mock-disabled-input">
                                    {formatCurrency(account.data.balance)}
                                </div>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    )
}

export default AccountOB